import React from 'react';
import { Tooltip } from 'antd';
import { MenuOutlined, PlusOutlined, LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { SceneListItem } from './scene-list-item';
import { SceneState } from '../../types/heygen';
import { C } from '../../constants/design-tokens';
import { useResponsiveSizes } from '../../../../hooks/use-responsive-sizes';
import type { ProjectScene } from '../../../../../../constructs/avatars/get-avatar-project-details/interface';

export const SCENE_PANEL_WIDTH_EXPANDED = 280;
export const SCENE_PANEL_WIDTH_COLLAPSED = 64;

interface SceneListPanelProps {
  scenes: SceneState[];
  activeIndex: number;
  projectScenes: ProjectScene[];
  onSelectScene: (index: number) => void;
  onAddScene: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

const CollapsedStatusIcon: React.FC<{ status?: string }> = ({ status }) => {
  if (!status || status === 'pending') return null;
  if (status === 'video_processing') return <LoadingOutlined style={{ fontSize: 10, color: C.warning }} />;
  if (status === 'completed') return <CheckCircleOutlined style={{ fontSize: 10, color: C.success }} />;
  if (status === 'failed') return <CloseCircleOutlined style={{ fontSize: 10, color: C.danger }} />;
  return null;
};

export const SceneListPanel: React.FC<SceneListPanelProps> = ({
  scenes,
  activeIndex,
  projectScenes,
  onSelectScene,
  onAddScene,
  collapsed,
  onToggleCollapse
}) => {
  const { spacing, fontSize, iconSize } = useResponsiveSizes();

  if (collapsed) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', alignItems: 'center' }}>
        {/* Toggle button */}
        <div style={{ padding: `${spacing.md}px 0 ${spacing.sm}px`, flexShrink: 0 }}>
          <Tooltip title='Expandir escenas' placement='right'>
            <MenuOutlined
              onClick={onToggleCollapse}
              style={{ fontSize: iconSize.md, color: C.textMuted, cursor: 'pointer' }}
            />
          </Tooltip>
        </div>

        {/* Scene dots + add button (scrollable together) */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: spacing.xs,
            padding: `${spacing.xs}px 0`
          }}
        >
          {scenes.map((_, i) => {
            const sceneStatus = projectScenes.find(s => s.index === i)?.status;
            return (
              <Tooltip key={i} title={`Escena ${String(i + 1).padStart(2, '0')}`} placement='right'>
                <div
                  onClick={() => onSelectScene(i)}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: fontSize.xs,
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: i === activeIndex ? C.accentSurface : 'transparent',
                    color: i === activeIndex ? C.accent : C.textSecondary,
                    border: i === activeIndex ? `2px solid ${C.accent}` : `1px solid ${C.border}`,
                    transition: 'all 0.15s ease',
                    position: 'relative'
                  }}
                >
                  {String(i + 1).padStart(2, '0')}
                  {sceneStatus && sceneStatus !== 'pending' && (
                    <div style={{ position: 'absolute', top: -2, right: -2 }}>
                      <CollapsedStatusIcon status={sceneStatus} />
                    </div>
                  )}
                </div>
              </Tooltip>
            );
          })}

          {/* Add scene — inside scroll, below last dot */}
          <Tooltip title='Agregar escena' placement='right'>
            <div
              onClick={onAddScene}
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                flexShrink: 0,
                color: C.accent,
                border: `1px dashed ${C.accentBorder}`,
                transition: 'background 0.15s ease'
              }}
              onMouseEnter={e => (e.currentTarget.style.background = C.accentSurface)}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <PlusOutlined style={{ fontSize: iconSize.sm }} />
            </div>
          </Tooltip>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: `${spacing.md}px ${spacing.md}px ${spacing.sm}px`,
          flexShrink: 0
        }}
      >
        <span
          style={{
            fontSize: fontSize.sm,       // template: .col__title 12px
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            color: C.textLabel
          }}
        >
          Escenas · {scenes.length}
        </span>
        <Tooltip title='Colapsar'>
          <MenuOutlined
            onClick={onToggleCollapse}
            style={{ fontSize: iconSize.sm, color: C.textMuted, cursor: 'pointer' }}
          />
        </Tooltip>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: `0 ${spacing.xs}px` }}>
        {scenes.map((scene, i) => (
          <SceneListItem
            key={i}
            scene={scene}
            index={i}
            isActive={i === activeIndex}
            processingStatus={projectScenes.find(s => s.index === i)?.status}
            onSelect={() => onSelectScene(i)}
          />
        ))}

        <div style={{ padding: `${spacing.sm}px ${spacing.xs}px` }}>
          <div
            onClick={onAddScene}
            style={{
              padding: `${spacing.sm}px ${spacing.sm}px`,
              borderRadius: 8,
              cursor: 'pointer',
              color: C.textSecondary,
              fontSize: fontSize.md,       // template: .scene-add 13px
              fontWeight: 500,
              textAlign: 'center',
              border: `1px dashed ${C.accentBorder}`,
              transition: 'background 0.15s ease'
            }}
            onMouseEnter={e => (e.currentTarget.style.background = C.accentSurface)}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            + Agregar escena
          </div>
        </div>
      </div>
    </div>
  );
};
